import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	CheckboxProps,
	Divider,
	FormControlLabel,
	Checkbox as MuiCheckbox,
	TableCell as MuiTableCell,
	Radio,
	Table,
	TableBody,
	TableCellProps,
	TableHead,
	TableRow,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { CodeBlock } from 'app/main/test/CodeBlock';
import { BadgeChipX } from 'framework/components/chips/BadgeChipX';
import { CollapseX } from 'framework/components/CollapseX';
import { NotApplicableEm } from 'framework/components/not-applicable.em';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { Trigger } from 'framework/hooks/useTrigger';
import { isLast } from 'framework/utils/array/isLast';
import { formatDate } from 'framework/utils/date/formatDate';
import { formatCurrency } from 'framework/utils/formatCurrency';
import { useAccordionState } from 'framework/utils/useAccordionState';
import {
	downloadAcousoftDocument_handle,
	FrequencyIntensity,
	FrequencyIntensitys,
	FrequencyPeriodicity,
	IAcousoftDocumentData,
	IAcousoftHpData,
	IAcousoftIntakeData,
	IAcousoftIntakeGroupData,
	IAcousoftIntakeQA,
	IAcousoftKlantSummary,
	IAcousoftMemoData,
	IAcousoftSaleData,
	IAcousoftSalesLineData,
	IHpCosiQuestionnaire,
	IHpPostQuestionnaire,
	IHpPreQuestionnaire,
	IHpQuestPart1,
	IHpQuestPart2,
	IHpQuestPart3,
	IntensityLevel,
	IntensityLevels,
	patientsQuery_acousoft,
} from 'gen/ApiClient';
import { useDownload } from 'gen/useDownload';
import { useLocalization } from 'localization/useLocalization';
import React, { Fragment, useMemo } from 'react';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';

type AccordionType = 'details' | 'memos' | 'documents' | 'sales' | 'intakes' | 'hps';

type AcousoftTabProps = {
	patientId: string;
	reloadTrigger: Trigger;
};

export const AcousoftTab = ({ patientId, reloadTrigger }: AcousoftTabProps) => {
	const [item, reload] = useApiEffect(patientsQuery_acousoft, patientId);
	const [expandedAccordion, setExpandedAccordion] = useAccordionState<AccordionType>();

	useLazyEffect(() => {
		reload();
	}, [reloadTrigger]);

	if (!item) {
		return <></>;
	}

	return (
		<div className='df-col'>
			<Accordion
				expanded={expandedAccordion === 'details'}
				onChange={() => setExpandedAccordion('details')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Details`}</span>
					<BadgeChipX
						value={item.klanten.length}
						color={item.klanten.length > 1 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails>
					{item.klanten.map(klant => (
						<Fragment key={klant.klantID}>
							<KlantListItem item={klant} />
							{isLast(klant, item.klanten) === false && <Divider />}
						</Fragment>
					))}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'memos'}
				onChange={() => setExpandedAccordion('memos')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Memos`}</span>
					<BadgeChipX
						value={item.memos.length}
						color={item.memos.length > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.memos.map(memo => (
						<Fragment key={memo.klant_MemoID}>
							<MemoListItem item={memo} />
							{isLast(memo, item.memos) === false && <Divider />}
						</Fragment>
					))}
					{item.memos.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'documents'}
				onChange={() => setExpandedAccordion('documents')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Documenten`}</span>
					<BadgeChipX
						value={item.documents.length}
						color={item.documents.length > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.documents.map(doc => (
						<Fragment key={doc.klant_DocumentID}>
							<DocumentListItem item={doc} />
							{isLast(doc, item.documents) === false && <Divider />}
						</Fragment>
					))}
					{item.documents.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'sales'}
				onChange={() => setExpandedAccordion('sales')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Verkopen`}</span>
					<BadgeChipX
						value={item.sales.length}
						color={item.sales.length > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.sales.map(sale => (
						<Fragment key={sale.id}>
							<SaleListItem item={sale} />
							{isLast(sale, item.sales) === false && <Divider />}
						</Fragment>
					))}
					{item.sales.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'intakes'}
				onChange={() => setExpandedAccordion('intakes')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Intakes`}</span>
					<BadgeChipX
						value={item.intakes.length}
						color={item.intakes.length > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.intakes.map(intake => (
						<Fragment key={intake.id}>
							<IntakeListItem item={intake} />
							{isLast(intake, item.intakes) === false && <Divider />}
						</Fragment>
					))}
					{item.intakes.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'hps'}
				onChange={() => setExpandedAccordion('hps')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Hoorprotocollen`}</span>
					<BadgeChipX
						value={item.hoorprotocols.length}
						color={item.hoorprotocols.length > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.hoorprotocols.map(hp => (
						<Fragment key={hp.hP2_HeaderID}>
							<HpListItem item={hp} />
							{isLast(hp, item.hoorprotocols) === false && <Divider />}
						</Fragment>
					))}
					{item.hoorprotocols.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
		</div>
	);
};

type KlantListItemProps = {
	item: IAcousoftKlantSummary;
};

const KlantListItem = ({ item }: KlantListItemProps) => {
	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{item.formatted}</div>
			</div>
			<div style={{ whiteSpace: 'pre-line' }}>{`Klant ID: ${item.klantID}`}</div>
			<CollapseX collapseAfter>
				<CodeBlock value={{ ...item, formatted: '' }} />
			</CollapseX>
		</div>
	);
};

type MemoListItemProps = {
	item: IAcousoftMemoData;
};

const MemoListItem = ({ item }: MemoListItemProps) => {
	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{`${formatDate(item.datum, 'only-date')}: ${item.onderwerp}`}</div>
			</div>
			<div style={{ whiteSpace: 'pre-line' }}>{item.memo}</div>
			<CollapseX collapseAfter>
				<CodeBlock value={item} />
			</CollapseX>
		</div>
	);
};

type DocumentListItemProps = {
	item: IAcousoftDocumentData;
};

const DocumentListItem = ({ item }: DocumentListItemProps) => {
	const [download, isDownloading] = useDownload(downloadAcousoftDocument_handle);

	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{`${formatDate(item.documentDatum, 'only-date')}: ${item.documentTypeDescription}`}</div>
			</div>
			<div style={{ whiteSpace: 'pre-line' }}>{item.documentnaam}</div>
			<div>
				<Button
					startIcon={<GetAppOutlinedIcon />}
					onClick={() => download(item.klant_DocumentID)}
					disabled={item.hasAttachment === false || isDownloading}
					color='primary'>
					{`Download`}
				</Button>
			</div>
			<CollapseX collapseAfter>
				<CodeBlock value={item} />
			</CollapseX>
		</div>
	);
};

function formatHeader(item: IAcousoftSaleData) {
	let header = '';
	if (item.factuurdatum) {
		header += formatDate(item.factuurdatum, 'only-date');
	}
	if (item.klantFactuurnummer) {
		header += ` ${item.klantFactuurnummer}`;
		header = header.trim();
	}
	if (item.zorgFactuurnummer) {
		header += ` ${item.zorgFactuurnummer}`;
		header = header.trim();
	}
	return Boolean(header) ? header : 'n/a';
}

type SaleListItemProps = {
	item: IAcousoftSaleData;
};

const SaleListItem = ({ item }: SaleListItemProps) => {
	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{formatHeader(item) ?? 'n/a'}</div>
			</div>
			<div style={{ whiteSpace: 'pre-line' }}>{`Klantfactuurnr: ${item.klantFactuurnummer ?? 'n/a'} | ${formatCurrency(item.klantFactuurbedrag ?? 0)}`}</div>
			<div style={{ whiteSpace: 'pre-line' }}>{`Zorgfactuurnr: ${item.zorgFactuurnummer ?? 'n/a'} | ${formatCurrency(item.zorgFactuurbedrag ?? 0)}`}</div>
			<CollapseX collapseAfter>
				<CodeBlock value={{ ...item, lines: [] }} />
			</CollapseX>
			<div style={{ fontWeight: 'bold' }}>{`Lijnen`}</div>
			{item.lines.length === 0 && <NotApplicableEm />}
			{item.lines.map(line => (
				<SalesLineListItem
					item={line}
					key={line.id}
				/>
			))}
		</div>
	);
};

type SalesLineListItemProps = {
	item: IAcousoftSalesLineData;
};

const SalesLineListItem = ({ item }: SalesLineListItemProps) => {
	return (
		<CollapseX
			collapseAfter
			expandText={`${item.formatted} ${formatCurrency(item.prijs ?? 0)}`}>
			<CodeBlock value={item} />
		</CollapseX>
	);
};

type IntakeListItemProps = {
	item: IAcousoftIntakeData;
};

const IntakeListItem = ({ item }: IntakeListItemProps) => {
	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{formatDate(item.intakeDatum, 'only-date')}</div>
			</div>
			<div>{`Type: ${item.type}`}</div>
			<div>{`Omschrijving: ${item.omschrijving}`}</div>
			<CollapseX collapseAfter>
				<CodeBlock value={{ ...item, groups: [] }} />
			</CollapseX>
			{item.groups.length === 0 && <NotApplicableEm />}
			{item.groups.map((group, index) => (
				<IntakeGroupListItem
					item={group}
					key={group.id}
					zbi={index}
				/>
			))}
		</div>
	);
};

type IntakeGroupListItemProps = {
	item: IAcousoftIntakeGroupData;
	zbi: number;
};

const IntakeGroupListItem = ({ item, zbi }: IntakeGroupListItemProps) => {
	return (
		<div className='df-col gap-4'>
			<div style={{ fontWeight: 'bold' }}>{`${zbi + 1}. ${item.naam}`}</div>
			{item.qAs.length === 0 && <NotApplicableEm />}
			{item.qAs.map(qa => (
				<IntakeQAListItem
					item={qa}
					key={qa.antwoordId}
				/>
			))}
		</div>
	);
};

type IntakeQAListItemProps = {
	item: IAcousoftIntakeQA;
};

const IntakeQAListItem = ({ item }: IntakeQAListItemProps) => {
	return (
		<div
			className='df-col gap-4'
			style={{ marginLeft: 16 }}>
			<div style={{}}>{`${item.volgorde}) ${item.vraag}: ${item.antwoord}`}</div>
			{/* <div style={{ whiteSpace: 'pre-line' }}>{item.antwoord}</div> */}
		</div>

		// <CollapseX
		// 	collapseAfter
		// 	expandText={`${item.volgorde}) ${item.vraag}`}>
		// 	<CodeBlock value={item.antwoord} />
		// </CollapseX>
	);
};

// questions.ts
const QuestionDefinitions: Record<number, string> = {
	1: 'Kunt u het geluid van verschillende muziekinstrumenten onderscheiden?',
	2: 'Kunt u aan de klank van iemands stem horen of iemand boos of blij is?',
	3: 'Kunt u het ritme in een muziekstuk of lied horen?',
	4: 'Kunt u iemand verstaan die u aanspreekt op een verjaardagsfeest of een receptie?',
	5: 'Hoort u op straat vanuit welke richting een auto aan komt rijden?',
	6: 'Kunt u iemand die u aanspreekt in een drukke straat verstaan?',
	7: 'Zeggen anderen in uw omgeving dat uw radio hard aanstaat?',
	8: 'Kunt u een tv-presentator alleen aan zijn stem herkennen?',
	9: 'Vindt u het storend als de radio aanstaat tijdens een gesprek?',
	10: 'Ervaart u dat muziek te hard klinkt, terwijl het voor omstanders aangenaam klinkt?',
	11: 'Kunt u de kassamedewerker in een drukke winkel verstaan?',
	12: 'Wanneer u met meerdere mensen aan tafel zit te eten, kunt u het gesprek dan volgen?',
	13: 'Kunt u met één persoon een gesprek voeren in een rustige omgeving?',
	14: 'Kunt u een telefoongesprek voeren in een rustige kamer?',
	15: 'Kunt u in een rustig huis horen waarvandaan iemand tot u spreekt?',
	16: 'Herkent u verschillende familieleden aan hun stem?',
	17: "Kunt u auto's horen die passeren of aan komen rijden?",
	18: 'Kunt u in uw eigen taal gezongen tekst verstaan?',
	19: 'Kunt u mannenstemmen van vrouwenstemmen onderscheiden?',
	20: 'Kunt u zonder veel inspanning iemand verstaan die naast u in de bus of in de auto zit?',
	21: 'Hoort u de geluiden thuis (stofzuiger, wasmachine, stromend water)?',
	22: 'Kunt u thuis de deurbel horen?',
	23: 'Kunt u een melodie in een muziekstuk of een lied herkennen?',
	24: 'Kunt u buiten de vogels horen fluiten?',
	25: 'Hoort u het wanneer iemand van achteren naar u toe komt lopen?',
	26: 'Vermindert u het volume van de radio, of schakelt u deze volledig uit om een gesprek gemakkelijker te maken?',
	27: 'Hoort u op straat waar zich een toeterende auto bevindt?',
	28: 'Kijkt u de goede kant op als u op straat door iemand geroepen wordt?',
	29: 'Wordt tegen u gezegd dat uw tv hard aanstaat wanneer u tv kijkt?',
	30: 'Ervaart u geluiden in het huishouden als erg hinderlijk?',
	31: 'Kunt u op straat het geluid van een auto onderscheiden van het geluid van een bus?',
	32: 'Kunt u horen vanuit welke hoek van de zaal een vraag gesteld wordt tijdens een bijeenkomst?',
	36: 'Draagt u hoortoestellen',
	63: 'Deze ruimte kunt u gebruiken voor vragen/punten die u zelf nog wilt bespreken met de audicien',
	65: 'Hoeveel moeite moet u doen om anderen te verstaan?',
	66: 'Heeft u last van vermoeidheid aan het einde van de dag door uw hoorprobleem?',
	67: 'Hoeveel last heeft u van uw hoorprobleem?',
	68: 'Beïnvloedt uw hoorprobleem uw sociale leven?',
	69: 'Beïnvloedt uw hoorprobleem uw functioneren op het werk, school en/of activiteiten?',
	70: 'Ik heb de vragen zelfstandig ingevuld',
	71: 'Waar heeft u deze vragenlijst ingevuld',
};

const FrequencyIntensityRecord: Record<FrequencyIntensity, string> = {
	AlmostNever: 'Bijna nooit',
	Always: 'Altijd',
	NotApplicable: 'N.v.t.',
	Often: 'Vaak',
	Sometimes: 'Soms',
};

const FrequencyPeriodicityRecord: Record<FrequencyPeriodicity, string> = {
	Daily: 'Dagelijks',
	Weekly: 'Wekelijks',
	Monthly: 'Maandelijks',
};

const IntensityLevelRecord: Record<IntensityLevel, string> = {
	NotApplicable: 'N.v.t.',
	VeryMuch: 'Heel erg',
	Much: 'Erg',
	Somewhat: 'Enigszins',
	None: 'Helemaal niet',
};

type HpListItemProps = {
	item: IAcousoftHpData;
};

const HpListItem = ({ item }: HpListItemProps) => {
	const stripped = useMemo(
		() => ({ ...item, preQuestionnaire: undefined, postCosi: undefined, postQuestionnaire: undefined, preCosi: undefined, handtekening: undefined }),
		[item]
	);

	return (
		<div className='df-col gap-4'>
			<div className='df-row-ac gap-4'>
				<FiberManualRecordIcon fontSize='small' />
				<div style={{ fontWeight: 'bold' }}>{`ID: ${item.hP2_HeaderID}, rekendatum: ${formatDate(item.rekenDatum, 'only-date')}, categorie: ${
					item.categorieCode
				}`}</div>
			</div>
			<CollapseX collapseAfter>
				<CodeBlock value={stripped} />
			</CollapseX>
			<div style={{ fontWeight: Boolean(item.preQuestionnaire) ? 'bold' : 'unset' }}>{`1) Hoorvragenlijst Pré`}</div>
			{item.preQuestionnaire && <PreQStack item={item.preQuestionnaire} />}
			<Divider />
			<div style={{ fontWeight: Boolean(item.preCosi) ? 'bold' : 'unset' }}>{`2) COSI Pré`}</div>
			{item.preCosi && (
				<CollapseX>
					<CosiTable item={item.preCosi} />
				</CollapseX>
			)}
			<Divider />
			<div style={{ fontWeight: Boolean(item.postQuestionnaire) ? 'bold' : 'unset' }}>{`3) Hoorvragenlijst Post`}</div>
			{item.postQuestionnaire && <PostQStack item={item.postQuestionnaire} />}
			<Divider />
			<div style={{ fontWeight: Boolean(item.postCosi) ? 'bold' : 'unset' }}>{`4) COSI Post (optioneel)`}</div>
			{item.postCosi && (
				<CollapseX>
					<CosiTable item={item.postCosi} />
				</CollapseX>
			)}
			<div style={{ fontWeight: Boolean(item.handtekening) ? 'bold' : 'unset' }}>{`Handtekening`}</div>
			{Boolean(item.handtekening) && (
				<img
					src={item.handtekening}
					alt='Handtekening'
					style={{ maxWidth: 200, maxHeight: 200 }}
				/>
			)}
		</div>
	);
};

type CosiTableProps = {
	item: IHpCosiQuestionnaire;
};

const CosiTable = ({ item }: CosiTableProps) => {
	return (
		<>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>{`Specifieke wens tot verbetering`}</TableCell>
						<TableCell>{`Prioriteit`}</TableCell>
						<TableCell align='center'>{`Detecteren van geluid`}</TableCell>
						<TableCell align='center'>{`Verstaan in stilte`}</TableCell>
						<TableCell align='center'>{`Verstaan in ruis`}</TableCell>
						<TableCell align='center'>{`Ruistolerantie`}</TableCell>
						<TableCell align='center'>{`Focus/discriminatie`}</TableCell>
						<TableCell align='center'>{`Localiseren van geluiden`}</TableCell>
						<TableCell align='center'>{`Overige hoorhulp middelen`}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{item.entries.map((entry, index) => (
						<TableRow key={index}>
							<TableCell>{entry.situation}</TableCell>
							<TableCell>{entry.priority}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('SoundDetection')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('SpeechReceptionWithoutNoise')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('SpeechReceptionWithNoise')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('NoiseTolerance')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('Focus')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.domains.includes('Localization')} />}</TableCell>
							<TableCell align='center'>{<Checkbox checked={entry.otherHearingAids} />}</TableCell>
						</TableRow>
					))}
					<TableRow>
						<TableCell>{`Doelstellingen`}</TableCell>
						<TableCell>{``}</TableCell>
						<TableCell align='center'>{item.targets.domain1}</TableCell>
						<TableCell align='center'>{item.targets.domain2}</TableCell>
						<TableCell align='center'>{item.targets.domain3}</TableCell>
						<TableCell align='center'>{item.targets.domain4}</TableCell>
						<TableCell align='center'>{item.targets.domain5}</TableCell>
						<TableCell align='center'>{item.targets.domain6}</TableCell>
						<TableCell align='center'>{``}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</>
	);
};

const Checkbox = (props: CheckboxProps) => {
	return (
		<MuiCheckbox
			size='small'
			style={{ padding: 0 }}
			{...props}
		/>
	);
};

const TableCell = (props: TableCellProps) => {
	return (
		<MuiTableCell
			style={{ padding: 4, fontSize: 12 }}
			{...props}
		/>
	);
};

type PreQStackProps = {
	item: IHpPreQuestionnaire;
};

const PreQStack = ({ item }: PreQStackProps) => {
	return (
		<>
			<HpQuestPart1CollapseX item={item.part1} />
			<HpQuestPart2CollapseX item={item.part2} />
			<HpQuestPart3CollapseX item={item.part3} />
		</>
	);
};

type PostQStackProps = {
	item: IHpPostQuestionnaire;
};

const PostQStack = ({ item }: PostQStackProps) => {
	return (
		<>
			<HpQuestPart1CollapseX item={item.part1} />
			<HpQuestPart3CollapseX item={item.part3} />
		</>
	);
};

type HpQuestPart1PRops = {
	item: IHpQuestPart1;
};

const HpQuestPart1CollapseX = ({ item }: HpQuestPart1PRops) => {
	const strings = useLocalization();

	return (
		<CollapseX
			collapseAfter
			expandText='Deel 1'
			collapseText='Verberg deel 1'>
			<div
				className='df-col gap-8'
				style={{ marginLeft: 16 }}>
				<div>
					{`${QuestionDefinitions[36]} : `}
					<strong>{item.currentlyWearingHearingAids ? strings.yes : strings.no}</strong>{' '}
				</div>
				<Divider />
				{item.entries.map((entry, index) => (
					<React.Fragment key={index}>
						<div
							className='df-col'
							key={index}>
							<div>{`${QuestionDefinitions[entry.question]} : `}</div>
							<div
								className='df-row-ac gap-4'
								style={{ marginLeft: 8 }}>
								{FrequencyIntensitys.map(fi => (
									<FormControlLabel
										control={
											<Radio
												color='primary'
												checked={fi === entry.answer}
												size='small'
												style={{ padding: 0, paddingRight: 4 }}
											/>
										}
										label={FrequencyIntensityRecord[fi]}
										labelPlacement='end'
									/>
								))}
							</div>
						</div>
						{isLast(entry, item.entries) === false && <Divider />}
					</React.Fragment>
				))}
			</div>
		</CollapseX>
	);
};

type HpQuestPart2CollapseXProps = {
	item: IHpQuestPart2;
};

const HpQuestPart2CollapseX = ({ item }: HpQuestPart2CollapseXProps) => {
	return (
		<CollapseX
			collapseAfter
			expandText='Deel 2'
			collapseText='Verberg deel 2'>
			<div
				className='df-col'
				style={{ marginLeft: 16 }}>
				{item.entries.map(entry => (
					<div>
						{`Situatie: ${entry.situation} : `}
						<strong>{FrequencyPeriodicityRecord[entry.answer]}</strong>
					</div>
				))}
				<div>
					{`Andere dingen te bespreken met de audicien : `}
					<strong>{item.otherThingsToDiscuss}</strong>
				</div>
			</div>
		</CollapseX>
	);
};

type HpQuestPart3CollapseXProps = {
	item: IHpQuestPart3;
};

const HpQuestPart3CollapseX = ({ item }: HpQuestPart3CollapseXProps) => {
	const strings = useLocalization();

	return (
		<CollapseX
			collapseAfter
			expandText='Deel 3'
			collapseText='Verberg deel 3'>
			<div
				className='df-col gap-8'
				style={{ marginLeft: 16 }}>
				{item.questions.map((entry, index) => (
					<React.Fragment key={index}>
						<div className='df-col'>
							<div>{`${QuestionDefinitions[entry.question]} : `}</div>
							<div
								className='df-row-ac gap-4'
								style={{ marginLeft: 8 }}>
								{IntensityLevels.map(fi => (
									<FormControlLabel
										control={
											<Radio
												color='primary'
												checked={fi === entry.answer}
												size='small'
												style={{ padding: 0, paddingRight: 4 }}
											/>
										}
										label={IntensityLevelRecord[fi]}
										labelPlacement='end'
									/>
								))}
							</div>
						</div>
						{isLast(entry, item.questions) === false && <Divider />}
					</React.Fragment>
				))}
				<Divider />
				<div>
					{`Ik heb alle vragen zelfstandig ingevuld : `}
					<strong>{`${item.answeredQuestionsIndependently ? strings.yes : strings.no} (anders: ${item.whoHelpedAnsweringTheQuestions})`}</strong>
				</div>
				<Divider />
				<div>
					{`Waar heeft u deze vragenlijst ingevuld? :`}
					<strong>{item.place}</strong>
				</div>
			</div>
		</CollapseX>
	);
};
